import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useReviewQueueStore } from '@/hooks/useStores';
import { formatStoryDate } from '@/types/story';
import { formatRelativeTime } from '@/utils/formatRelativeTime';

interface OverviewPendingReviewCardProps {
  familyId: string;
  canReview: boolean;
  onViewAll: () => void;
}

const OverviewPendingReviewCard: React.FC<OverviewPendingReviewCardProps> = observer(
  ({ familyId, canReview, onViewAll }) => {
    const { t } = useTranslation('overview');
    const reviewQueueStore = useReviewQueueStore();

    const loadSummary = useCallback(async () => {
      if (!canReview) return;
      await reviewQueueStore.loadPendingSummary(familyId);
    }, [canReview, familyId, reviewQueueStore]);

    useEffect(() => {
      void loadSummary();
    }, [loadSummary]);

    const getItemTypeLabel = (itemType: string): string =>
      t(`pendingReview.itemType.${itemType}`, { defaultValue: itemType });

    if (!canReview) return null;

    const pendingCount = reviewQueueStore.pendingSummaryFamilyId === familyId ? reviewQueueStore.pendingTotal : 0;
    const preview = reviewQueueStore.pendingSummaryFamilyId === familyId ? reviewQueueStore.pendingPreview : [];

    if (reviewQueueStore.isLoadingPendingSummary) {
      return (
        <section className="overview-card" aria-labelledby="overview-pending-review-title">
          <header className="overview-card__head">
            <h2 id="overview-pending-review-title" className="overview-card__title">
              {t('pendingReview.title')}
            </h2>
          </header>
          <p className="overview-card__empty">{t('pendingReview.loading')}</p>
        </section>
      );
    }

    if (pendingCount <= 0) return null;

    return (
      <section className="overview-card" aria-labelledby="overview-pending-review-title">
        <header className="overview-card__head">
          <h2 id="overview-pending-review-title" className="overview-card__title">
            {t('pendingReview.title')}
            <span className="overview-card__count overview-card__count--alert">{pendingCount}</span>
          </h2>
          <button type="button" className="overview-card__more" onClick={onViewAll}>
            {t('pendingReview.review')}
          </button>
        </header>
        <div className="overview-card__body">
          {preview.map((item) => (
            <div key={`${item.itemType}-${item.id}`} className="review-item">
              <div>
                <div className="review-item__name">{item.title || t('pendingReview.untitled')}</div>
                <div className="review-item__meta">
                  {getItemTypeLabel(item.itemType)}
                  {item.targetLabel ? ` · ${item.targetLabel}` : ''}
                  {' · '}
                  {formatRelativeTime(item.createdAt)}
                </div>
              </div>
              <div className="review-item__date">{formatStoryDate(item.createdAt)}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }
);

export default OverviewPendingReviewCard;
